import { Variant } from '../../../../../../../../../models/product/dto/variant';
import { ProductType } from '../../../../../../../../../models/enum/dto/product-type.enum';
import { VariantType } from '../../../../../../../../../models/enum/dto/variant-type.enum';
import { VariantTypeUtils } from '../../../../../../../../../utils/variant-type-utils';
import { exists } from '../../../../../../../../../functions/exists';
import { SectionRowViewModel } from './SectionRowViewModel';
import { memoize } from '../../../../../../../../shared/decorators/memoize';

export class SectionRowViewModelSizeAndQuantity extends SectionRowViewModel {
  public override getSize(variant?: Variant): string {
    const v = variant || this.rowVariants?.firstOrNull();
    switch (true) {
      case ProductType.Seed === v?.productType || VariantType.Seed === v?.variantType:
        return this.getSizeHelperForSeeds(v);
      case VariantTypeUtils.isPreRollType(v?.variantType):
        return this.getSizeHelperForPreRolls(v);
      case VariantTypeUtils.isCapsuleType(v?.variantType):
        return this.getSizeHelperForCapsules(v);
      case v?.productType === ProductType.Edible && !VariantTypeUtils.isReadyToDrinkBeverageType(v?.variantType):
        return this.getSizeHelperForEdiblesThatAreNotReadyToDrinkBeverages(v);
      case v?.packagedQuantity > 1:
        return this.getSizeHelperForEverythingElseThatHasAPackagedQuantityGreaterThanOne(v);
      default:
        return this.getSizeHelperForEverythingElseThatHasAPackagedQuantityEqualToOne(v);
    }
  }

  protected getSizeHelperForSeeds(variant: Variant): string | null {
    return null;
  }

  protected getSizeHelperForPreRolls(variant: Variant): string | null {
    const quantity = variant?.packagedQuantity;
    const formattedUnitSize = this.getFormattedUnitSizeFor(variant);
    const hasQty = Number.isFinite(quantity) && quantity > 0;
    const hasSize =
      exists(formattedUnitSize) && formattedUnitSize !== '0' && formattedUnitSize !== '-' && formattedUnitSize !== '--';
    switch (true) {
      case hasQty && hasSize:
        return `${quantity} x ${formattedUnitSize}`;
      case hasQty && !hasSize:
        return `${quantity}`;
      default:
        return '-';
    }
  }

  protected getSizeHelperForCapsules(variant: Variant): string | null {
    const n = variant?.packagedQuantity ?? 0;
    if (n < 1) return '-';
    return `${n} cap`
      .pluralizer()
      .addRule({ isPlural: n > 1, useApostrophe: false, word: 'cap' })
      .pluralize();
  }

  protected getSizeHelperForEdiblesThatAreNotReadyToDrinkBeverages(variant: Variant): string | null {
    return `${variant?.packagedQuantity} ${this.edibleNonReadyToDrinkPackageQuantityWord(variant)}`;
  }

  protected getSizeHelperForEverythingElseThatHasAPackagedQuantityGreaterThanOne(variant: Variant): string | null {
    return `${variant?.packagedQuantity} ${this.quantityWordForEverythingElse(variant)}`;
  }

  protected getSizeHelperForEverythingElseThatHasAPackagedQuantityEqualToOne(variant: Variant): string | null {
    return super.getSize(variant);
  }

  public getGridSize(variant?: Variant): string {
    const v = variant || this.rowVariants?.firstOrNull();
    switch (true) {
      case ProductType.Seed === v?.productType || VariantType.Seed === v?.variantType:
        return this.getGridSizeHelperForSeeds(v);
      case VariantTypeUtils.isPreRollType(v?.variantType):
        return this.getGridSizeHelperForPreRolls(v);
      case VariantTypeUtils.isCapsuleType(v?.variantType):
        return this.getGridSizeHelperForCapsules(v);
      case v?.productType === ProductType.Beverage && VariantTypeUtils.isReadyToDrinkBeverageType(v?.variantType):
        return this.getGridSizeHelperForReadyToDrinkBeverages(v);
      case v?.productType === ProductType.Edible && !VariantTypeUtils.isReadyToDrinkBeverageType(v?.variantType):
        return this.getGridSizeHelperForEdiblesThatAreNotReadyToDrinkBeverages(v);
      default:
        return this.getGridSizeHelperForEverythingElse(v);
    }
  }

  protected getGridSizeHelperForSeeds(variant: Variant): string | null {
    switch (true) {
      case exists(variant?.packagedQuantity):
        return `${variant?.packagedQuantity} ${this.seedPackageQuantityWord(variant)}`;
      default:
        return null;
    }
  }

  protected getGridSizeHelperForPreRolls(variant: Variant): string | null {
    const quantity = variant?.packagedQuantity;
    const formattedUnitSize = this.getFormattedUnitSizeFor(variant);
    const hasQty = Number.isFinite(quantity);
    const hasSize = exists(formattedUnitSize) && formattedUnitSize !== '-';
    switch (true) {
      case hasQty && hasSize:
        return `${quantity} x ${formattedUnitSize}`;
      case hasQty && !hasSize:
        return `${quantity}`;
      default:
        return '-';
    }
  }

  protected getGridSizeHelperForCapsules(variant: Variant): string | null {
    const n = variant?.packagedQuantity ?? 0;
    return `${n} cap`
      .pluralizer()
      .addRule({ isPlural: n > 1, useApostrophe: false, word: 'cap' })
      .pluralize();
  }

  protected getGridSizeHelperForReadyToDrinkBeverages(variant: Variant): string | null {
    const quantity = variant?.packagedQuantity;
    const formattedUnitSize = this.getFormattedUnitSizeFor(variant);
    switch (true) {
      case !quantity:
      case quantity === 1:
        return formattedUnitSize;
      default:
        return `${quantity} x ${formattedUnitSize}`;
    }
  }

  protected getGridSizeHelperForEdiblesThatAreNotReadyToDrinkBeverages(variant: Variant): string | null {
    const quantity = variant?.packagedQuantity;
    switch (true) {
      case !quantity:
        return this.getFormattedUnitSizeFor(variant);
      default:
        return `${variant?.packagedQuantity} ${this.edibleNonReadyToDrinkPackageQuantityWord(variant)}`;
    }
  }

  protected getGridSizeHelperForEverythingElse(variant: Variant): string | null {
    return this.getFormattedUnitSizeFor(variant);
  }

  protected seedPackageQuantityWord(variant: Variant): string {
    return 'pk';
  }

  protected edibleNonReadyToDrinkPackageQuantityWord(variant: Variant): string {
    return 'pc';
  }

  protected quantityWordForEverythingElse(variant: Variant): string {
    return 'pc';
  }

  getFormattedUnitSizeFor(variant: Variant): string {
    return variant?.getFormattedUnitSize();
  }

  @memoize
  public override getQuantityAndSizeString(variant?: Variant): string {
    const rowVariant = variant || this.rowVariants?.firstOrNull();
    const productType = rowVariant?.productType;
    const variantType = rowVariant?.variantType;
    const packagedQuantity = this.getQuantity(variant);
    const size = this.getSize(variant);
    const quantity = this.getQuantityString(variant);
    const onlyShowSize = (pt: ProductType, vt: VariantType): boolean => {
      return (
        pt === ProductType.Seed ||
        vt === VariantType.Seed ||
        VariantTypeUtils.isPreRollType(vt) ||
        VariantTypeUtils.isCapsuleType(vt) ||
        pt === ProductType.Edible ||
        VariantTypeUtils.isReadyToDrinkBeverageType(vt)
      );
    };
    const hasSize = exists(size) && size !== '0' && size !== '-' && size !== '--';
    switch (true) {
      case !hasSize:
      case !packagedQuantity && !hasSize:
        return null;
      case packagedQuantity === 1 && hasSize:
      case onlyShowSize(productType, variantType):
        return size;
      default:
        return [quantity, size].filter(s => exists(s)).join(' x ');
    }
  }

  public getGridQuantityAndSizeString(variant?: Variant): string {
    const rowVariant = variant || this.rowVariants?.firstOrNull();
    const productType = rowVariant?.productType;
    const variantType = rowVariant?.variantType;
    const packagedQuantity = this.getGridQuantity(variant);
    const size = this.getGridSize(variant);
    const quantity = this.getQuantityString(variant);
    const onlyShowSize = (pt: ProductType, vt: VariantType): boolean => {
      return (
        pt === ProductType.Seed ||
        vt === VariantType.Seed ||
        VariantTypeUtils.isPreRollType(vt) ||
        VariantTypeUtils.isCapsuleType(vt) ||
        pt === ProductType.Edible ||
        VariantTypeUtils.isReadyToDrinkBeverageType(vt)
      );
    };
    const hasSize = exists(size) && size !== '0' && size !== '-' && size !== '--';
    switch (true) {
      case !hasSize:
      case !packagedQuantity && !hasSize:
        return null;
      case packagedQuantity === 1 && hasSize:
      case onlyShowSize(productType, variantType):
        return size;
      default:
        return [quantity, size].filter(s => exists(s)).join(' x ');
    }
  }
}
